<script lang="ts">
  // Modal de detalhe — bracket estilo chaveamento de torneio (colunas
  // horizontais, vencedor destacado, linhas conectando rounds).
  // Auto-refresh: subscribe no store `tournaments` (alimentado por WS
  // tournament:state em stores/lobby.ts) — Felipe 2026-05-27 reclamou
  // que bracket ficava "travado" sem F5; agora atualiza sozinho.
  import type { Tournament, BracketMatch } from '../../stores/tournaments';
  import { tournaments } from '../../stores/tournaments';
  import { userId } from '../../stores/auth';

  type Props = { tournament: Tournament; onClose: () => void };
  let { tournament: initialT, onClose }: Props = $props();

  // Usa versao do store se existir (atualiza via WS), senao fallback pra prop
  let t = $derived.by<Tournament>(() => {
    const fromStore = $tournaments.find((x) => x.id === initialT.id);
    return fromStore ?? initialT;
  });

  function phaseLabel(roundIdx: number, matchesInRound: number): string {
    // matchesInRound × playersPerMatch = total inicial de jogadores envolvidos.
    // Os nomes brasileiros tradicionais (oitavas/quartas/semi/final) sao em
    // funcao do numero de duplas/players que CABE na fase (8avos=16, 4tos=8, etc).
    // Como suportamos ate 256 players, ampliamos a tabela.
    if (matchesInRound === 1) return 'Final';
    if (matchesInRound === 2) return 'Semifinal';
    if (matchesInRound === 4) return 'Quartas';
    if (matchesInRound === 8) return 'Oitavas';
    if (matchesInRound === 16) return '16-avos';
    if (matchesInRound === 32) return '32-avos';
    if (matchesInRound === 64) return '64-avos';
    return `Fase ${roundIdx + 1}`;
  }

  // Retorna o nome do player/bot. Player atual marca como "VOCÊ" pra
  // reconhecer; demais (humanos OU bots) usam player_names[pid] do
  // backend. Fallback pra bot_names legacy (torneios pre-2026-05-25).
  function pidName(pid: string): string {
    if (pid === $userId) return 'VOCÊ';
    const fromNew = t.player_names?.[pid];
    if (fromNew) return fromNew;
    const fromLegacy = t.bot_names?.[pid];
    if (fromLegacy) return fromLegacy;
    if (pid.startsWith('bot-')) return 'bot';
    return pid.slice(0, 8);
  }

  function matchStateLabel(bm: BracketMatch): string {
    if (bm.winner) return ''; // resolvido, sem label extra (winner ja destacado)
    if (bm.match_id) return 'em andamento';
    return 'aguardando';
  }

  // Champion = dupla vencedora (2v2) ou player solo (1v1). Pega da final
  // do bracket (ultima round, 1a match) e retorna o time vencedor inteiro.
  let championTeam = $derived.by<string[]>(() => {
    if (t.status !== 'finished' || !t.bracket?.rounds?.length) return [];
    const finalRound = t.bracket.rounds[t.bracket.rounds.length - 1];
    const finalMatch = finalRound.matches[0];
    if (!finalMatch || !finalMatch.winner) return [];
    return finalMatch.winner === 'A' ? finalMatch.team_a : finalMatch.team_b;
  });

  // ── Pan & zoom (estilo visualizador de imagem) ──────────────────
  // Bracket pode ser largo demais pro celular (ate 8 rounds em torneio
  // 256). Em vez de scroll nativo desajeitado, tratamos como canvas:
  // arrasta pra mover, pinca/botoes pra zoom. O transform fica em
  // .bracket-pan; .bracket em si NAO escala — assim os conectores SVG,
  // medidos em coords de layout (offset*), batem certo em qualquer zoom.
  let viewportEl = $state<HTMLDivElement | null>(null);
  let bracketEl = $state<HTMLDivElement | null>(null);
  let scale = $state(1);
  let tx = $state(0);
  let ty = $state(0);
  let fitted = false;

  const MIN_SCALE = 0.25;
  const MAX_SCALE = 2.5;
  const clampScale = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

  const pointers = new Map<number, { x: number; y: number }>();
  let lastDist = 0;

  // Ajusta o zoom pra largura toda do bracket caber na viewport, centrado.
  // Retorna true se aplicou (dimensoes validas), false se ainda nao deu —
  // o caller usa pra saber se precisa tentar de novo num frame futuro.
  function fitToWidth(): boolean {
    if (!viewportEl || !bracketEl) return false;
    const vw = viewportEl.clientWidth;
    const bw = bracketEl.scrollWidth;
    if (vw === 0 || bw === 0) return false;
    const s = clampScale(Math.min(1, vw / bw));
    scale = s;
    tx = Math.max(0, (vw - bw * s) / 2);
    ty = 0;
    return true;
  }

  // Zoom mantendo fixo o ponto (localX, localY) em coords da viewport.
  // screen = t + s*content  =>  t1 = M - (s1/s0)*(M - t0).
  function zoomAbout(localX: number, localY: number, factor: number) {
    const s1 = clampScale(scale * factor);
    const k = s1 / scale;
    tx = localX - k * (localX - tx);
    ty = localY - k * (localY - ty);
    scale = s1;
  }

  function zoomBtn(factor: number) {
    if (!viewportEl) return;
    zoomAbout(viewportEl.clientWidth / 2, viewportEl.clientHeight / 2, factor);
  }

  function onPointerDown(e: PointerEvent) {
    viewportEl?.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      lastDist = Math.hypot(a.x - b.x, a.y - b.y);
    }
  }

  function onPointerMove(e: PointerEvent) {
    if (!pointers.has(e.pointerId) || !viewportEl) return;
    const prev = pointers.get(e.pointerId)!;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pts = [...pointers.values()];
    if (pts.length === 1) {
      // arrasta: move a tela
      tx += e.clientX - prev.x;
      ty += e.clientY - prev.y;
    } else if (pts.length >= 2) {
      // pinca: zoom no ponto medio dos dois dedos
      const [a, b] = pts;
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (lastDist > 0) {
        const rect = viewportEl.getBoundingClientRect();
        const midX = (a.x + b.x) / 2 - rect.left;
        const midY = (a.y + b.y) / 2 - rect.top;
        zoomAbout(midX, midY, dist / lastDist);
      }
      lastDist = dist;
    }
  }

  function onPointerUp(e: PointerEvent) {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) lastDist = 0;
  }

  function onWheel(e: WheelEvent) {
    if (!viewportEl) return;
    e.preventDefault();
    const rect = viewportEl.getBoundingClientRect();
    zoomAbout(e.clientX - rect.left, e.clientY - rect.top, e.deltaY < 0 ? 1.12 : 0.89);
  }

  // SVG overlay com paths bracket. Medimos posicoes via offset* (layout,
  // imune ao transform de zoom) somando a cadeia de offsetParent ate o
  // .bracket. CSS L-connector sozinho quebra com gaps variaveis.
  type ConnectorPath = { d: string; key: string };
  let connectors = $state<ConnectorPath[]>([]);
  let svgW = $state(0);
  let svgH = $state(0);

  function offsetWithin(el: HTMLElement, root: HTMLElement) {
    let x = 0;
    let y = 0;
    let n: HTMLElement | null = el;
    while (n && n !== root) {
      x += n.offsetLeft;
      y += n.offsetTop;
      n = n.offsetParent as HTMLElement | null;
    }
    return { x, y };
  }

  function recomputeConnectors() {
    if (!bracketEl || !t.bracket?.rounds?.length) {
      connectors = [];
      return;
    }
    svgW = bracketEl.scrollWidth;
    svgH = bracketEl.scrollHeight;
    const paths: ConnectorPath[] = [];
    const rounds = t.bracket.rounds;
    for (let r = 0; r < rounds.length - 1; r++) {
      const round = rounds[r];
      for (let i = 0; i < round.matches.length; i++) {
        const bm = round.matches[i];
        if (!bm.winner) continue;
        const nextIdx = Math.floor(i / 2);
        const nextRound = rounds[r + 1];
        if (!nextRound || !nextRound.matches[nextIdx]) continue;
        const nextBm = nextRound.matches[nextIdx];
        const srcEl = bracketEl.querySelector(`[data-bm-id="${bm.id}"]`) as HTMLElement | null;
        const tgtEl = bracketEl.querySelector(`[data-bm-id="${nextBm.id}"]`) as HTMLElement | null;
        if (!srcEl || !tgtEl) continue;
        const teamEl = srcEl.querySelector(`[data-team="${bm.winner}"]`) as HTMLElement | null;
        if (!teamEl) continue;
        const so = offsetWithin(srcEl, bracketEl);
        const to = offsetWithin(tgtEl, bracketEl);
        const wo = offsetWithin(teamEl, bracketEl);
        const x1 = so.x + srcEl.offsetWidth;
        const y1 = wo.y + teamEl.offsetHeight / 2;
        const x2 = to.x;
        const y2 = to.y + tgtEl.offsetHeight / 2;
        const midX = (x1 + x2) / 2;
        // Path em S/L: horizontal -> vertical -> horizontal
        const d = `M ${x1} ${y1} H ${midX} V ${y2} H ${x2}`;
        paths.push({ d, key: `${bm.id}->${nextBm.id}` });
      }
    }
    connectors = paths;
  }

  $effect(() => {
    // Re-run quando bracket muda (winner novo, slot novo populado, ...)
    void t.bracket?.rounds;
    void t.updated_at;
    if (!bracketEl) return;
    // Espera 1 frame pra DOM atualizar antes de medir
    requestAnimationFrame(() => {
      recomputeConnectors();
      if (!fitted) fitted = fitToWidth();
    });
  });

  // Observa redimensionamento DO BRACKET e DA VIEWPORT pra re-medir
  // conectores e tentar fit ate dar certo (na 1a abertura do modal o
  // scrollWidth pode vir 0 antes do layout estabilizar — sem isso fica
  // travado em scale=initial e o "FINAL" some).
  $effect(() => {
    if (!bracketEl || !viewportEl) return;
    const ro = new ResizeObserver(() => {
      recomputeConnectors();
      if (!fitted) fitted = fitToWidth();
    });
    ro.observe(bracketEl);
    ro.observe(viewportEl);
    return () => ro.disconnect();
  });
</script>

<div class="overlay" onclick={onClose} role="presentation">
  <div class="card" onclick={(e) => e.stopPropagation()} role="dialog">
    <header>
      <div>
        <h2>{t.name || `Torneio ${t.format.toUpperCase()}`}</h2>
        <div class="meta">
          <span class="status status-{t.status}">{t.status}</span>
          <span>{t.size} players</span>
          <span class="modality">{t.modality}</span>
        </div>
      </div>
      <button class="close" onclick={onClose} aria-label="fechar">×</button>
    </header>

    {#if t.status === 'finished' && championTeam.length > 0}
      <div class="champion-banner">
        <svg class="trophy" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M6 4h12v4a6 6 0 0 1-12 0V4z" stroke="currentColor" stroke-width="1.5" fill="rgba(255,210,74,0.18)"/>
          <path d="M6 6H4a2 2 0 0 0 0 4h2M18 6h2a2 2 0 0 1 0 4h-2" stroke="currentColor" stroke-width="1.5"/>
          <path d="M10 14h4v3h2v2H8v-2h2v-3z" fill="currentColor" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>
        </svg>
        <div class="champion-text">
          <span class="champion-label">{championTeam.length > 1 ? 'DUPLA CAMPEÃ' : 'CAMPEÃO'}</span>
          <strong>
            {#each championTeam as pid, i}
              {pidName(pid)}{#if i < championTeam.length - 1}<span class="amp"> + </span>{/if}
            {/each}
          </strong>
        </div>
      </div>
    {/if}

    {#if !t.bracket?.rounds?.length}
      <div class="empty">Sem bracket gerado ainda.</div>
    {:else}
      <div
        class="bracket-viewport"
        bind:this={viewportEl}
        onpointerdown={onPointerDown}
        onpointermove={onPointerMove}
        onpointerup={onPointerUp}
        onpointercancel={onPointerUp}
        onwheel={onWheel}
        role="application"
        aria-label="chaveamento — arraste pra mover, pinça ou botões pra dar zoom"
      >
        <div class="bracket-pan" style="transform: translate({tx}px, {ty}px) scale({scale});">
        <div class="bracket" bind:this={bracketEl}>
          <svg class="connectors-svg" width={svgW} height={svgH} aria-hidden="true">
            {#each connectors as c (c.key)}
              <path d={c.d} class="connector" />
            {/each}
          </svg>
          {#each t.bracket.rounds as round (round.index)}
            {@const hasLive = round.matches.some((m) => !!m.match_id && !m.winner)}
            {@const isCurrent = t.status === 'running' && hasLive}
            <div class="round-col" class:current={isCurrent}>
              <div class="round-head">
                <span class="phase">{phaseLabel(round.index, round.matches.length)}</span>
                {#if isCurrent}<span class="badge-now">AGORA</span>{/if}
              </div>
              <div class="matches">
                {#each round.matches as bm (bm.id)}
                  {@const teamAEmpty = !bm.team_a || bm.team_a.length === 0}
                  {@const teamBEmpty = !bm.team_b || bm.team_b.length === 0}
                  {@const pending = teamAEmpty || teamBEmpty}
                  <div class="match"
                    data-bm-id={bm.id}
                    class:decided={!!bm.winner}
                    class:pending
                    class:winner-a={bm.winner === 'A'}
                    class:winner-b={bm.winner === 'B'}>
                    <div class="team" data-team="A" class:winner={bm.winner === 'A'} class:loser={bm.winner === 'B'}>
                      <div class="names">
                        {#if teamAEmpty}
                          <span class="tbd">aguardando</span>
                        {:else}
                          {#each bm.team_a as pid}
                            <span class="name" title={pidName(pid)}>{pidName(pid)}</span>
                          {/each}
                        {/if}
                      </div>
                      {#if bm.winner === 'A'}<span class="check">✓</span>{/if}
                    </div>
                    <div class="team" data-team="B" class:winner={bm.winner === 'B'} class:loser={bm.winner === 'A'}>
                      <div class="names">
                        {#if teamBEmpty}
                          <span class="tbd">aguardando</span>
                        {:else}
                          {#each bm.team_b as pid}
                            <span class="name" title={pidName(pid)}>{pidName(pid)}</span>
                          {/each}
                        {/if}
                      </div>
                      {#if bm.winner === 'B'}<span class="check">✓</span>{/if}
                    </div>
                    {#if matchStateLabel(bm)}
                      <div class="match-state">{matchStateLabel(bm)}</div>
                    {/if}
                  </div>
                {/each}
              </div>
            </div>
          {/each}
        </div>
        </div>
        <div class="zoom-controls">
          <button type="button" onclick={() => zoomBtn(1.25)} aria-label="aproximar">+</button>
          <button type="button" onclick={() => zoomBtn(0.8)} aria-label="afastar">−</button>
          <button type="button" class="fit" onclick={fitToWidth} aria-label="ajustar à tela">⤢</button>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .overlay {
    position: fixed;
    inset: 0;
    z-index: 1100;
    background: rgba(2, 3, 26, 0.85);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
  }
  .card {
    width: 100%;
    max-width: 900px;
    max-height: calc(100dvh - 32px);
    overflow: hidden;
    background: linear-gradient(180deg, #faf5ec 0%, #fff 60%, #f0e7d5 100%);
    border: 4px solid #0D47A1;
    outline: 2px solid #ffb300;
    outline-offset: -3px;
    border-radius: 14px;
    box-shadow: 0 6px 0 #051a3a, 0 18px 50px rgba(0, 0, 0, 0.5);
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  @media (max-width: 520px) {
    .overlay { padding: 8px; }
    .card { padding: 12px 12px; gap: 10px; }
    h2 { font-size: 17px; }
    .meta { font-size: 10px; gap: 6px; }
  }
  header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  }
  h2 {
    margin: 0 0 6px;
    font-size: 20px;
    font-weight: 900;
    color: #0D47A1;
    letter-spacing: 0.5px;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
  }
  .meta { display: flex; gap: 8px; font-size: 11px; color: #1976D2; flex-wrap: wrap; font-weight: 700; align-items: center; }
  .status {
    text-transform: uppercase;
    letter-spacing: 1px;
    background: #1565C0;
    color: #fff;
    padding: 3px 10px;
    border-radius: 999px;
    font-weight: 900;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
    box-shadow: 0 2px 0 #0D47A1;
  }
  .status-finished {
    background: linear-gradient(180deg, #66bb6a, #43a047);
    box-shadow: 0 2px 0 #1b5e20;
  }
  .status-running {
    background: linear-gradient(180deg, #ffb300, #f57c00);
    box-shadow: 0 2px 0 #e65100;
  }
  .modality { text-transform: uppercase; letter-spacing: 1px; color: #0D47A1; }
  .close {
    background: #fff;
    border: 2px solid #1565C0;
    color: #1565C0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    font-size: 18px;
    font-weight: 900;
    line-height: 1;
    cursor: pointer;
    box-shadow: 0 2px 0 #0D47A1;
    transition: all 0.1s;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .close:hover {
    background: #e3f2fd;
    transform: translateY(-1px);
    box-shadow: 0 3px 0 #0D47A1;
  }
  .close:active {
    transform: translateY(1px);
    box-shadow: 0 1px 0 #0D47A1;
  }

  .champion-banner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    background: linear-gradient(180deg, #fff7ad 0%, #ffd54f 50%, #f9a825 100%);
    border: 3px solid #e65100;
    border-radius: 12px;
    padding: 14px 18px;
    color: #5d3a00;
    box-shadow: 0 3px 0 #b85a00, inset 0 1px 0 rgba(255, 255, 255, 0.5);
  }
  .trophy {
    width: 44px;
    height: 44px;
    color: #ff8f00;
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.25));
    flex-shrink: 0;
  }
  .champion-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    line-height: 1.25;
    min-width: 0;       /* deixa o flex item encolher sem empurrar */
    flex: 1;
  }
  .champion-label {
    font-size: 11px;
    letter-spacing: 2.5px;
    text-transform: uppercase;
    color: #8b4513;
    font-weight: 800;
  }
  .champion-text strong {
    font-size: 18px;
    letter-spacing: 1px;
    color: #4a148c;
    font-weight: 900;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
    /* Nicks tipo "chiquinhaDaViDa" sao palavras unicas que nao quebram —
       sem isso o banner vaza pra fora do modal. */
    overflow-wrap: anywhere;
    word-break: break-word;
  }
  .amp { color: #2e7d32; margin: 0 4px; }

  @media (max-width: 480px) {
    .champion-banner { padding: 10px 12px; gap: 10px; }
    .trophy { width: 34px; height: 34px; }
    .champion-text strong { font-size: 15px; letter-spacing: 0.5px; }
    .champion-label { letter-spacing: 1.8px; font-size: 10px; }
  }

  .empty {
    padding: 20px;
    border: 2px dashed #1565C0;
    background: rgba(21, 101, 192, 0.05);
    border-radius: 10px;
    text-align: center;
    color: #1976D2;
    font-size: 13px;
    font-weight: 700;
  }

  /* Bracket — canvas pan/zoom (estilo visualizador de imagem).
     .bracket-viewport = janela que recorta; .bracket-pan = camada que
     recebe transform (translate+scale); .bracket = conteudo em coords
     de layout. touch-action:none deixa a gente tratar pinca/arrasto. */
  .bracket-viewport {
    position: relative;
    flex: 1;
    min-height: 300px;
    overflow: hidden;
    border-radius: 10px;
    border: 2px solid rgba(21, 101, 192, 0.25);
    background:
      radial-gradient(circle at 20% 20%, rgba(21, 101, 192, 0.06), transparent 60%),
      rgba(13, 71, 161, 0.03);
    touch-action: none;
    cursor: grab;
    user-select: none;
    -webkit-user-select: none;
  }
  .bracket-viewport:active { cursor: grabbing; }
  .bracket-pan {
    position: absolute;
    top: 0;
    left: 0;
    transform-origin: 0 0;
    width: max-content;
    will-change: transform;
    padding: 12px;
  }
  .bracket {
    display: flex;
    gap: 50px; /* gap pra acomodar as linhas conectoras 22+22=44 + folga */
    align-items: stretch;
  }

  .zoom-controls {
    position: absolute;
    right: 10px;
    bottom: 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    z-index: 5;
  }
  .zoom-controls button {
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 900;
    line-height: 1;
    color: #1565C0;
    background: #fff;
    border: 2px solid #1565C0;
    border-radius: 10px;
    box-shadow: 0 2px 0 #0D47A1;
    cursor: pointer;
    transition: transform 0.1s, background 0.1s;
  }
  .zoom-controls button:hover { background: #e3f2fd; }
  .zoom-controls button:active { transform: translateY(2px); box-shadow: 0 0 0 #0D47A1; }
  .zoom-controls .fit { font-size: 16px; }
  .round-col {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 220px;
    flex-shrink: 0;
    position: relative;
  }
  @media (max-width: 520px) {
    .bracket { gap: 32px; }
    .round-col { min-width: 160px; }
  }
  .round-col.current .round-head .phase {
    color: #e65100;
  }
  .round-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    background: linear-gradient(180deg, #1976D2 0%, #1565C0 100%);
    color: #fff;
    border: 2px solid #0D47A1;
    border-radius: 8px;
    box-shadow: 0 2px 0 #0D47A1, inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }
  .round-head .phase {
    font-size: 12px;
    font-weight: 900;
    letter-spacing: 1.8px;
    text-transform: uppercase;
    color: #ffd54f;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.4);
  }
  .badge-now {
    background: linear-gradient(180deg, #ffb300, #f57c00);
    color: #fff;
    font-size: 9px;
    font-weight: 900;
    padding: 2px 8px;
    border-radius: 999px;
    letter-spacing: 1px;
    text-shadow: 0 1px 0 rgba(0, 0, 0, 0.3);
    box-shadow: 0 1px 0 #e65100;
  }

  /* As matches sao distribuidas verticalmente espalhadas em cada round
     — emula visual de chaveamento com cada round "menor" centrada
     em relacao a anterior */
  .matches {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    flex: 1;
    gap: 12px;
  }
  .match {
    background: linear-gradient(180deg, #faf5ec 0%, #fff 100%);
    border: 2.5px solid #1565C0;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
    box-shadow: 0 2px 0 #0D47A1, 0 4px 8px rgba(13, 71, 161, 0.15);
  }
  .match.decided {
    border-color: #2e7d32;
    box-shadow: 0 2px 0 #1b5e20, 0 4px 8px rgba(46, 125, 50, 0.18);
  }
  .match.pending {
    border-color: #b0bec5;
    background: rgba(207, 216, 220, 0.4);
    box-shadow: 0 2px 0 #78909c;
  }
  .tbd {
    color: #78909c;
    font-style: italic;
    font-size: 11px;
    font-weight: 600;
  }
  .team {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    font-size: 12px;
    color: #1976D2;
    border-bottom: 2px solid #d9c9a8;
    min-width: 0;
    font-weight: 700;
  }
  .team:last-of-type {
    border-bottom: none;
  }
  .team .names {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }
  .team .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.2;
    color: #0D47A1;
  }
  .team.winner {
    color: #1b5e20;
    background: linear-gradient(90deg, rgba(76, 175, 80, 0.25), rgba(76, 175, 80, 0.08));
    font-weight: 900;
  }
  .team.winner .name {
    color: #1b5e20;
  }
  .team.winner .check {
    color: #2e7d32;
    font-weight: 900;
    font-size: 16px;
    flex-shrink: 0;
  }
  .team.loser {
    text-decoration: line-through;
    background: linear-gradient(90deg, rgba(229, 57, 113, 0.18), rgba(229, 57, 113, 0.04));
    color: #ad1457;
  }
  .team.loser .name {
    color: #c2185b;
    font-weight: 700;
  }
  .match-state {
    text-align: center;
    font-size: 9px;
    color: #fff;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 4px 0;
    background: linear-gradient(180deg, #ffb300, #f57c00);
    font-weight: 900;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
  }

  /* SVG overlay com paths calculados via JS — mede posicoes reais dos
     cards via getBoundingClientRect e desenha L precisa winner -> next.
     Robusto pra rounds com gaps variaveis (space-around). */
  .bracket { position: relative; }
  .connectors-svg {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    z-index: 0;
    overflow: visible;
  }
  .connectors-svg .connector {
    fill: none;
    stroke: #2e7d32;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
    filter: drop-shadow(0 1px 1px rgba(46, 125, 50, 0.35));
  }
  .round-col { z-index: 1; }
</style>
